import {
    ChangeDetectionStrategy,
    Component,
    DestroyRef,
    Injector,
    afterNextRender,
    computed,
    inject,
    input,
    linkedSignal,
    output,
    signal,
    viewChild,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { EmblaCarouselType } from 'embla-carousel';
import { Subject } from 'rxjs';

import { TrackingEvents } from '../../../_shared/data/tracking-events.data';
import { IMediaItem, IMediaSlider } from '../../../_shared/models';
import { BackgroundVariant } from '../../../_shared/models/hero/hero-variant.enum';
import { CastPipe } from '../../../_shared/pipes';
import { GtmService } from '../../../_shared/services/gtm.service';
import { Modal2Component } from '../../../modal/modal2/modal2.component';
import { SliderComponent } from '../../../slider/slider.component';
import { ImageItemComponent } from '../../media-item/image-item/image-item.component';
import { VideoItemComponent } from '../../media-item/video-item/video-item.component';

enum ScrollDirection {
    Next,
    Previous,
}

@Component({
    selector: 'sirius-media-collection-modal',
    templateUrl: './media-collection-modal.component.html',
    styleUrls: ['./media-collection-modal.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    imports: [Modal2Component, SliderComponent, ImageItemComponent, VideoItemComponent, CastPipe],
    host: {
        '[class.is-dark]': "variant() === 'dark'",
        '(keydown.arrowright)': 'onHostArrowKey($any($event), true)',
        '(keydown.arrowleft)': 'onHostArrowKey($any($event), false)',
    },
})
export class MediaCollectionModalComponent {
    readonly config = input<IMediaSlider | undefined>(undefined);
    readonly items = input<IMediaItem[]>([]);
    readonly startIndex = input(0);
    readonly showDescription = input(false);
    /** Also re-colours the projected media descriptions to match the dialog surface. */
    readonly variant = input<BackgroundVariant>(BackgroundVariant.LIGHT);

    readonly selectedIndex = output<number>();
    readonly closeEvent = output<number>();

    readonly slider = viewChild(SliderComponent);

    readonly index = linkedSignal(() => this.startIndex());
    readonly modalOpen = signal(true);
    readonly titleId = 'sirius-media-collection-modal-title';

    readonly carousel = computed<EmblaCarouselType | undefined>(() => this.slider()?.carousel);

    readonly currentTitle = computed<string>(() => this.items()[this.index()]?.title ?? '');

    private readonly _gtmService = inject(GtmService);
    private readonly _destroyRef = inject(DestroyRef);
    private readonly _injector = inject(Injector);

    private readonly _scroll = new Subject<ScrollDirection>();
    private readonly _select = new Subject<number>();

    constructor() {
        this.addTracking();
    }

    setIndex(index: number): void {
        this.index.set(index);
    }

    onClose(): void {
        this.closeEvent.emit(this.index());
    }

    /**
     * The dialog opens with focus on the close button, outside the slider's
     * subtree, so the slider's own Left/Right host handlers never see keys
     * pressed there. Forward them from the modal host; skip keys the slider
     * already consumed (defaultPrevented) and keys aimed at interactive media.
     */
    protected onHostArrowKey(event: KeyboardEvent, forward: boolean): void {
        if (event.defaultPrevented) return;

        const target = event.target as HTMLElement | null;
        if (
            target &&
            (['INPUT', 'TEXTAREA', 'SELECT', 'VIDEO', 'IFRAME'].includes(target.tagName) || target.isContentEditable)
        ) {
            return;
        }

        event.preventDefault();
        if (forward) {
            this.slider()?.scrollNext();
        } else {
            this.slider()?.scrollPrevious();
        }
    }

    onModalOpen(): void {
        // The carousel was initialised while the dialog was `display: none`, so Embla holds
        // stale 0x0 measurements. Re-measure once the open dialog is in the DOM.
        afterNextRender(() => this.carousel()?.reInit(), { injector: this._injector });
    }

    onLoaded(): void {
        this.carousel()?.reInit();
    }

    onScrollNext(): void {
        this._scroll.next(ScrollDirection.Next);
    }

    onScrollPrevious(): void {
        this._scroll.next(ScrollDirection.Previous);
    }

    onClick(index: number): void {
        this._select.next(index);
    }

    onVideoClick(event: Event, index: number): void {
        event.preventDefault();
        event.stopPropagation();
        this.onClick(index);
    }

    private addTracking(): void {
        this._select.pipe(takeUntilDestroyed(this._destroyRef)).subscribe((x: number) => {
            const cfg = {
                ...this.items()[x].tracking?.parameters,
                index: x,
            };

            this._gtmService.track(TrackingEvents.gtm['carouselSelect']([cfg]));
        });

        this._scroll.pipe(takeUntilDestroyed(this._destroyRef)).subscribe((x: ScrollDirection) => {
            const func =
                x === ScrollDirection.Next
                    ? TrackingEvents.gtm['carouselNext']
                    : TrackingEvents.gtm['carouselPrevious'];

            const cfg = {
                ...this.config()?.tracking?.parameters,
            };

            this._gtmService.track(func([cfg]));
        });
    }
}
